import { ErrorOutline, MailOutline, Refresh, Search } from "@mui/icons-material";
import EmailAttachmentChips from "components/Email/EmailAttachmentChips";
import {
  Box,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Pagination,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import DOMPurify from "dompurify";
import { formatDisplayDateTime } from "utils/dateFormat";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { P } from "const/permissions";
import { fetchEmailLogs } from "utils/emailLogService";
import { hasPermission } from "utils/permissionUtils";
import ClientWhatsApp from "./ClientWhatsApp";

const ClientEmails = ({ clientId }) => {
  const canEmail = hasPermission(P.EMAIL.MESSAGE_READ);
  const canWhatsApp = hasPermission(P.MESSAGING.WHATSAPP_READ);
  const [channel, setChannel] = useState(canEmail ? "email" : "whatsapp");
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    page: 0,
    size: 10,
    sortBy: "sentAt",
    sortDirection: "DESC",
    search: "",
    content: "",
    clientId,
  });
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    setFilters((prev) => ({ ...prev, clientId, page: 0 }));
  }, [clientId]);

  const { data, isFetching, refetch } = useQuery(
    ["client-email-logs", filters],
    () => fetchEmailLogs(filters),
    {
      enabled: !!clientId && canEmail && channel === "email",
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  );

  const emails = data?.content || [];
  const selectedEmail = useMemo(
    () => emails.find((email) => email.id === selectedId) || emails[0] || null,
    [emails, selectedId]
  );

  useEffect(() => {
    if (emails.length > 0 && !emails.some((email) => email.id === selectedId)) {
      setSelectedId(emails[0].id);
    }
  }, [emails, selectedId]);

  // Deep link from the invoice email history: ?tab=emails&emailId=<id> opens
  // that send directly. Consumed once matched so a refresh behaves normally.
  useEffect(() => {
    const requestedId = searchParams.get("emailId");
    if (!requestedId || emails.length === 0) return;
    const match = emails.find((email) => String(email.id) === requestedId);
    if (match) {
      setSelectedId(match.id);
      const next = new URLSearchParams(searchParams);
      next.delete("emailId");
      setSearchParams(next, { replace: true });
    }
  }, [emails, searchParams, setSearchParams]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: key === "page" ? value : 0,
    }));
  };

  return (
    <div>
      {canEmail && canWhatsApp && (
        <Tabs value={channel} onChange={(_, value) => setChannel(value)} sx={{ mb: 2 }}>
          <Tab value="email" label="Email" />
          <Tab value="whatsapp" label="WhatsApp" />
        </Tabs>
      )}
      {channel === "whatsapp" && canWhatsApp ? (
        <ClientWhatsApp clientId={clientId} />
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
          <Paper
            elevation={0}
            className="xl:col-span-5 border border-app-border rounded-lg overflow-hidden bg-app-surface"
          >
            <div className="p-4 grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 border-b border-app-border">
              <TextField
                size="small"
                label="Search client emails"
                value={filters.search}
                onChange={(event) => updateFilter("search", event.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                size="small"
                label="Search content"
                value={filters.content}
                onChange={(event) => updateFilter("content", event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <Tooltip title="Refresh">
                <IconButton onClick={() => refetch()} disabled={isFetching}>
                  <Refresh />
                </IconButton>
              </Tooltip>
            </div>

            <div className="divide-y divide-slate-100 min-h-[380px] max-h-[560px] overflow-y-auto">
              {emails.length === 0 && (
                <div className="h-[380px] flex flex-col items-center justify-center text-center px-6">
                  <MailOutline className="text-slate-300 mb-2" sx={{ fontSize: 48 }} />
                  <Typography className="font-semibold text-app-text">No emails tracked</Typography>
                  <Typography variant="body2" className="text-app-text-muted">
                    Emails sent to this client will appear here.
                  </Typography>
                </div>
              )}

              {emails.map((email) => {
                const active = selectedEmail?.id === email.id;
                return (
                  <button
                    key={email.id}
                    type="button"
                    onClick={() => setSelectedId(email.id)}
                    className={`w-full text-left px-4 py-3 ${
                      active ? "bg-app-surface-hover" : "bg-app-surface hover:bg-app-surface-muted"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <Typography className="font-semibold text-app-text truncate">
                          {email.subject || "(No subject)"}
                        </Typography>
                        <Typography variant="body2" className="text-app-text-muted truncate">
                          From {email.fromEmail || "-"}
                        </Typography>
                      </div>
                      <Chip
                        size="small"
                        label={email.status || "UNKNOWN"}
                        color={email.status === "SUCCESS" ? "success" : "error"}
                        variant={email.status === "SUCCESS" ? "outlined" : "filled"}
                      />
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <Typography variant="caption" className="text-app-text-muted">
                        {formatDisplayDateTime(email.sentAt)}
                      </Typography>
                      <Stack direction="row" spacing={0.5}>
                        {(email.tags || []).slice(0, 2).map((tag) => (
                          <Chip key={tag} size="small" label={tag} variant="outlined" />
                        ))}
                      </Stack>
                    </div>
                  </button>
                );
              })}
            </div>
            <Divider />
            <Box className="px-4 py-3 flex items-center justify-between gap-2">
              <Typography variant="body2" className="text-app-text-muted">
                {data?.totalItems || 0} emails
              </Typography>
              <Pagination
                size="small"
                count={Math.max(data?.totalPages || 1, 1)}
                page={(data?.currentPage || 0) + 1}
                onChange={(_, page) => updateFilter("page", page - 1)}
                color="primary"
              />
            </Box>
          </Paper>

          <Paper
            elevation={0}
            className="xl:col-span-7 border border-app-border rounded-lg overflow-hidden bg-app-surface"
          >
            {!selectedEmail ? (
              <div className="min-h-[480px] flex flex-col items-center justify-center text-center px-6">
                <MailOutline className="text-slate-300 mb-2" sx={{ fontSize: 48 }} />
                <Typography className="font-semibold text-app-text">Select an email</Typography>
              </div>
            ) : (
              <>
                <div className="px-5 py-4 border-b border-app-border">
                  <Typography variant="h6" className="font-semibold text-app-text break-words">
                    {selectedEmail.subject || "(No subject)"}
                  </Typography>
                  <Typography variant="body2" className="text-app-text-muted mt-1">
                    {formatDisplayDateTime(selectedEmail.sentAt)}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" mt={2}>
                    {(selectedEmail.tags || []).map((tag) => (
                      <Chip key={tag} size="small" label={tag} variant="outlined" />
                    ))}
                  </Stack>
                </div>

                <div className="px-5 py-4 grid grid-cols-1 md:grid-cols-2 gap-3 bg-app-surface-muted/70 border-b border-app-border">
                  <div>
                    <Typography
                      variant="caption"
                      className="text-app-text-muted font-bold uppercase"
                    >
                      From
                    </Typography>
                    <Typography variant="body2" className="break-words">
                      {selectedEmail.fromEmail || "-"}
                    </Typography>
                  </div>
                  <div>
                    <Typography
                      variant="caption"
                      className="text-app-text-muted font-bold uppercase"
                    >
                      To
                    </Typography>
                    <Typography variant="body2" className="break-words">
                      {selectedEmail.recipientEmail || "-"}
                    </Typography>
                  </div>
                  <div>
                    <Typography
                      variant="caption"
                      className="text-app-text-muted font-bold uppercase"
                    >
                      Sent By
                    </Typography>
                    <Typography variant="body2" className="break-words">
                      {selectedEmail.sentByName || selectedEmail.sentByEmail || "-"}
                    </Typography>
                  </div>
                  <div>
                    <Typography
                      variant="caption"
                      className="text-app-text-muted font-bold uppercase"
                    >
                      Provider
                    </Typography>
                    <Typography variant="body2">{selectedEmail.provider || "-"}</Typography>
                  </div>
                  {selectedEmail.errorMessage && (
                    <div className="md:col-span-2 flex gap-2 p-3 rounded-md bg-red-50 text-red-700 border border-red-100">
                      <ErrorOutline fontSize="small" />
                      <Typography variant="body2">{selectedEmail.errorMessage}</Typography>
                    </div>
                  )}
                  {selectedEmail.attachmentKeys?.length > 0 && (
                    <div className="md:col-span-2">
                      <EmailAttachmentChips
                        logId={selectedEmail.id}
                        attachmentKeys={selectedEmail.attachmentKeys}
                      />
                    </div>
                  )}
                </div>

                <div className="p-5 overflow-y-auto max-h-[520px] min-h-[260px]">
                  <div
                    className="prose max-w-none text-sm text-app-text leading-relaxed"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(selectedEmail.body || "<p>No body captured.</p>"),
                    }}
                  />
                </div>
              </>
            )}
          </Paper>
        </div>
      )}
    </div>
  );
};

export default ClientEmails;
